import { useEffect, useState, Fragment } from "react";
import { Table, Spin } from "antd";
import { Typography, Link, useMediaQuery } from "@material-ui/core";
import Status from "../components/Status";
import moment from "moment";
import useGetRecentlyAddedTradeIn from "../hooks/useGetRecentlyAddedTradeIn";
import { useTranslation } from "react-i18next";
import { formatAppDateTime } from "../utils/formatAppDateTime";

const gridContainerStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  padding: 16,
  background: "#fff",
  borderRadius: 4,
  marginBottom: 10,
};

const gridItemStyleBold = {
  marginBottom: 16,
  fontSize: 13,
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "22px",
};

const gridItemStyle = {
  marginBottom: 16,
  fontSize: 13,
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "22px",
};

const RecentlyAddedTradeIn = () => {
  const isDesktop = useMediaQuery("(min-width:769px)");
  const { t, i18n } = useTranslation();
  const { data, isLoading, error } = useGetRecentlyAddedTradeIn();
  const [transformedData, setTransformedData] = useState([]);

  useEffect(() => {
    const transformedData = data?.data?.map((item) => ({
      key: item.id,
      tradeInId: item.ctiQuoteParent.headerExternalId,
      customerName: `${item.ctiQuoteParent.firstName} ${item.ctiQuoteParent.lastName}`,
      device: item?.item?.name,
      tradeInDate: item.created_at,
      status: item.deviceProcessingState.name,
    }));
    setTransformedData(transformedData);
  }, [data]);

  const columns = [
    {
      title: t("Trade-In No."),
      dataIndex: "tradeInId",
      sorter: {
        compare: (a, b) => a.tradeInId.localeCompare(b.tradeInId),
        multiple: 3,
      },
      render: (id) => <Link href={`/TradeInDetails/${id}`}>{id}</Link>,
    },
    {
      title: t("Customer Name"),
      dataIndex: "customerName",
      sorter: {
        compare: (a, b) => a.customerName.localeCompare(b.customerName),
        multiple: 3,
      },
    },
    {
      title: t("Device"),
      dataIndex: "device",
      sorter: {
        compare: (a, b) => a.device - b.device,
        multiple: 3,
      },
    },
    {
      title: t("Trade-In Date"),
      dataIndex: "tradeInDate",
      render: (date) =>
        formatAppDateTime(date, i18n.language, "MMM DD, YYYY, h:mm A"),
      sorter: {
        compare: (a, b) =>
          moment(a.tradeInDate).unix() - moment(b.tradeInDate).unix(),
        multiple: 3,
      },
    },
    {
      title: t("Status"),
      dataIndex: "status",
      sorter: {
        compare: (a, b) => a.status.localeCompare(b.status),
        multiple: 3,
      },
      render: (status) => {
        return <Status>{status}</Status>;
      },
    },
  ];

  const onChange = (pagination, filters, sorter) => {
    const { field, order } = sorter;
    let sortedData = transformedData;

    if (field && order) {
      sortedData = [...transformedData].sort((a, b) => {
        const aValue = a[field];
        const bValue = b[field];

        // Compare the values based on the data type of the field
        if (typeof aValue === "string" && aValue) {
          return aValue.localeCompare(bValue) * (order === "ascend" ? 1 : -1);
        } else if (typeof aValue === "number") {
          return (aValue - bValue) * (order === "ascend" ? 1 : -1);
        } else if (moment.isMoment(aValue) && moment.isMoment(bValue)) {
          return (
            (moment(aValue).unix() - moment(bValue).unix()) *
            (order === "ascend" ? 1 : -1)
          );
        }

        return 0; // Default case: no sorting applied
      });
    }

    // Update the data source with the sorted data
    setTransformedData(sortedData);
  };
  return isDesktop ? (
    <>
      <Typography
        style={{
          color: "#343a40",
          fontSize: 16,
          fontFamily: "Inter",
          fontStyle: "normal",
          fontWeight: 500,
          lineHeight: "normal",
          letterSpacing: "-0.01em",
          marginBottom: 18,
          marginTop: 43,
        }}
      >
        {t(`Recently added trade-ins`)}
      </Typography>
      <Table
        className="recently-tradein"
        pagination={false}
        loading={isLoading}
        columns={columns}
        dataSource={transformedData}
        onChange={onChange}
      />
      {error && <>Error fetching data</>}
    </>
  ) : (
    <Fragment>
      {isLoading ? (
        <div className="recently-added-loading">
          <Spin />
        </div>
      ) : (
        <Fragment>
          <Typography
            style={{
              color: "#343a40",
              fontSize: 16,
              fontFamily: "Inter",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "normal",
              letterSpacing: "-0.01em",
              marginBottom: 18,
              marginTop: 43,
            }}
          >
            {t(`Recently added trade-ins`)}
          </Typography>
          {data?.data?.map((item) => (
            <div key={item.id} style={gridContainerStyle}>
              <div style={gridItemStyleBold}>{t("Trade-In No.")}</div>
              <div style={gridItemStyle}>
                <Link
                  href={`/TradeInDetails/${item.ctiQuoteParent.headerExternalId}`}
                >
                  {item.ctiQuoteParent.headerExternalId}
                </Link>
              </div>
              <div style={gridItemStyleBold}>{t("Customer Name")}</div>
              <div
                style={gridItemStyle}
              >{`${item.ctiQuoteParent.firstName} ${item.ctiQuoteParent.lastName}`}</div>
              <div style={gridItemStyleBold}>{t("Device")}</div>
              <div style={gridItemStyle}>{item?.item?.name}</div>
              <div style={gridItemStyleBold}>{t("Trade-In Date")}</div>
              <div style={gridItemStyle}>
                {formatAppDateTime(
                  item.created_at,
                  i18n.language,
                  "MMM DD, YYYY, h:mm A"
                )}
              </div>
              <div style={gridItemStyleBold}>{t("Status")}</div>
              <div style={gridItemStyle}>
                <Status>{item.deviceProcessingState.name}</Status>
              </div>
            </div>
          ))}
        </Fragment>
      )}
    </Fragment>
  );
};

export default RecentlyAddedTradeIn;
